import { createWorkspace, createWorkspaceUser } from './flowdeskDataModel.js';

const toId = (value) => String(value ?? '');

const upsertListItem = (items, item) => {
  if (!item?.id) return items || [];
  const list = Array.isArray(items) ? items : [];
  const id = toId(item.id);
  const exists = list.some(entry => toId(entry.id) === id);
  if (!exists) return [item, ...list];
  return list.map(entry => (toId(entry.id) === id ? { ...entry, ...item } : entry));
};

const deleteListItem = (items, id) => (
  (Array.isArray(items) ? items : []).filter(entry => toId(entry.id) !== toId(id))
);

const upsertCompany = (companies, company) => {
  if (!company?.id) return companies || {};
  const currentCompanies = companies || {};
  return {
    ...currentCompanies,
    [company.id]: {
      ...(currentCompanies[company.id] || {}),
      ...company
    }
  };
};

const deleteCompany = (companies, companyId) => {
  const nextCompanies = { ...(companies || {}) };
  delete nextCompanies[companyId];
  return nextCompanies;
};

const updateCompanyArray = ({ companies, companyId, field, value, type }) => {
  if (!companyId) return companies || {};
  const currentCompanies = companies || {};
  const company = currentCompanies[companyId];
  if (!company) return currentCompanies;
  const name = typeof value === 'string' ? value : value?.name;
  if (!name) return currentCompanies;
  const list = Array.isArray(company[field]) ? company[field] : [];
  const nextList = type === 'delete'
    ? list.filter(item => item !== name)
    : (list.includes(name) ? list : [...list, name]);

  return {
    ...currentCompanies,
    [companyId]: {
      ...company,
      [field]: nextList
    }
  };
};

const updateCompanyObjectList = ({ companies, companyId, field, item, type }) => {
  if (!companyId) return companies || {};
  const currentCompanies = companies || {};
  const company = currentCompanies[companyId];
  if (!company) return currentCompanies;
  const list = Array.isArray(company[field]) ? company[field] : [];
  const nextList = type === 'delete'
    ? deleteListItem(list, item?.id)
    : upsertListItem(list, item);

  return {
    ...currentCompanies,
    [companyId]: {
      ...company,
      [field]: nextList
    }
  };
};

const updateTaskObjectList = ({ tasks, taskId, field, item, type }) => (
  (tasks || []).map(task => {
    if (toId(task.id) !== toId(taskId)) return task;
    const list = Array.isArray(task[field]) ? task[field] : [];
    return {
      ...task,
      [field]: type === 'delete' ? deleteListItem(list, item?.id) : upsertListItem(list, item)
    };
  })
);

const updateTaskImages = ({ tasks, taskId, item, type }) => (
  (tasks || []).map(task => {
    if (toId(task.id) !== toId(taskId)) return task;
    const fileUrl = item?.fileUrl || item?.file_url || item;
    const images = Array.isArray(task.images) ? task.images : [];
    return {
      ...task,
      images: type === 'delete'
        ? images.filter(image => image !== fileUrl)
        : (fileUrl && !images.includes(fileUrl) ? [...images, fileUrl] : images)
    };
  })
);

export const applyServerChangesToState = ({ changes, state }) => {
  const next = {
    tasks: state.tasks || [],
    companies: state.companies || {},
    workspaceUsers: state.workspaceUsers || [],
    currentUser: state.currentUser,
    currentWorkspace: state.currentWorkspace,
    soundSettings: state.soundSettings,
    healthSettings: state.healthSettings,
    governorate: state.governorate,
    theme: state.theme,
    uiDensity: state.uiDensity,
    lang: state.lang,
    prayerStatus: state.prayerStatus
  };

  (changes || []).forEach(change => {
    const payload = change.payload;
    const type = change.type;

    if (change.entity === 'tasks') {
      next.tasks = type === 'delete'
        ? deleteListItem(next.tasks, change.entityId)
        : upsertListItem(next.tasks, payload);
      return;
    }

    if (change.entity === 'companies') {
      next.companies = type === 'delete'
        ? deleteCompany(next.companies, change.entityId)
        : upsertCompany(next.companies, payload);
      return;
    }

    if (change.entity === 'workspaceUsers' || change.entity === 'currentUser') {
      const user = payload ? createWorkspaceUser(payload) : null;
      next.workspaceUsers = type === 'delete'
        ? deleteListItem(next.workspaceUsers, change.entityId)
        : upsertListItem(next.workspaceUsers, user);
      if (next.currentUser && user && toId(next.currentUser.id) === toId(user.id)) {
        next.currentUser = { ...next.currentUser, ...user };
      }
      return;
    }

    if (change.entity === 'workspace' && payload) {
      next.currentWorkspace = createWorkspace(payload);
      return;
    }

    if (change.entity === 'companyClients') {
      next.companies = updateCompanyArray({
        companies: next.companies,
        companyId: payload?.companyId || payload?.company_id,
        field: 'clients',
        value: payload,
        type
      });
      return;
    }

    if (change.entity === 'companyManagers') {
      next.companies = updateCompanyArray({
        companies: next.companies,
        companyId: payload?.companyId || payload?.company_id,
        field: 'managers',
        value: payload,
        type
      });
      return;
    }

    if (change.entity === 'attendanceLogs') {
      next.companies = updateCompanyObjectList({
        companies: next.companies,
        companyId: payload?.companyId || payload?.company_id,
        field: 'attendanceLogs',
        item: payload,
        type
      });
      return;
    }

    if (change.entity === 'taskSubtasks') {
      next.tasks = updateTaskObjectList({
        tasks: next.tasks,
        taskId: payload?.taskId || payload?.task_id,
        field: 'subtasks',
        item: payload,
        type
      });
      return;
    }

    if (change.entity === 'taskAttachments') {
      next.tasks = updateTaskImages({
        tasks: next.tasks,
        taskId: payload?.taskId || payload?.task_id,
        item: payload,
        type
      });
      return;
    }

    if (change.entity === 'userSettings' && payload && type !== 'delete') {
      next.lang = payload.language || payload.lang || next.lang;
      next.theme = payload.theme || next.theme;
      next.uiDensity = payload.uiDensity || payload.ui_density || next.uiDensity;
      next.governorate = payload.governorate || next.governorate;
      next.soundSettings = {
        ...(next.soundSettings || {}),
        adhanVoice: payload.adhanVoice || payload.adhan_voice || next.soundSettings?.adhanVoice,
        volume: Number(payload.soundVolume ?? payload.sound_volume ?? next.soundSettings?.volume ?? 1)
      };
      next.healthSettings = {
        ...(next.healthSettings || {}),
        enabled: Boolean(payload.healthEnabled ?? payload.health_enabled ?? next.healthSettings?.enabled ?? true),
        interval: Number(payload.healthInterval || payload.health_interval || next.healthSettings?.interval || 60)
      };
      return;
    }

    if (change.entity === 'prayerStatus' && payload?.data && type !== 'delete') {
      next.prayerStatus = payload.data;
    }
  });

  return next;
};
